import React from "react";
import Table from "react-bootstrap/Table";
import { styles } from "../styles/styles.js";
import * as motion from "motion/react-client";
import { hapticTap } from "../utils/haptics";
import { isNum, sum, EM_DASH } from "../utils/format.js";

// An unpriced market rendered as "()" reads as a formatting glitch. It is
// missing data, and it is the normal condition for competitions no configured
// book prices.
const price = (value) => (isNum(value) ? value : EM_DASH);
const line = (value, priceValue) =>
  isNum(value) ? `${value} (${price(priceValue)})` : EM_DASH;

const OddsTable = ({ fixture, selectedTeam, onClick, mode }) => {
    if (!fixture) return null;

    // These six functions grade a completed game and highlight the winning cell.
    // Every one of them did bare arithmetic on values that can be null, and
    // `null + null >= null` is `0 >= 0` — true. So an unplayed game highlighted
    // the home team as the moneyline winner, and a completed game with no line
    // was graded as though the spread were 0.
    //
    // B14 unit 6: the four moneyline comparisons were `>=`, so a drawn game
    // highlighted BOTH teams as moneyline winners at once. A moneyline is won
    // outright or not at all. The spread comparisons keep `>=` deliberately —
    // that matches how hydration counts a cover, and aligning the three
    // definitions of a push is B15's job, not a display change made in passing.
    //
    // getUnderStyle already carried a `fixture.winner &&` guard in Recents mode.
    // The other five did not; that asymmetry is the tell that this was hit once
    // and fixed in exactly one place.
    const settled = isNum(fixture.home_points) && isNum(fixture.away_points);

    const getHomeSpreadStyle = () => {
      if (mode === "Recents") {
        let teamPoints, opponentPoints, spread;
        if (selectedTeam === fixture.home_team) {
            teamPoints = fixture.home_points;
            opponentPoints = fixture.away_points;
            spread = fixture.home_spread;
            
            if (settled && isNum(spread) && teamPoints + spread >= opponentPoints) {
              return styles.Suggested;
            }
        }
      }
      if (mode === "HeadtoHead") {
        if (settled && isNum(fixture.home_spread) &&
            fixture.home_points + fixture.home_spread >= fixture.away_points) {
          return styles.result;
        }
      }
      return styles.Odds;
    };

  // Rendered only when a book priced the draw, so a two-way competition's table
  // is unchanged — same rows, same cells, same styles.
  const getDrawMLStyle = () => {
    if (settled && fixture.home_points === fixture.away_points) {
      return mode === "HeadtoHead" ? styles.result : styles.Suggested;
    }
    return styles.Odds;
  };

  const getHomeMLStyle = () => {
    if (mode === "Recents") {
      let teamPoints, opponentPoints;
      if (selectedTeam === fixture.home_team) {
        teamPoints = fixture.home_points;
        opponentPoints = fixture.away_points;
        
        if (settled && teamPoints > opponentPoints) {
          return styles.Suggested;
        }
      }
    }

    if (mode === "HeadtoHead") {
      if (settled && fixture.home_points > fixture.away_points) {
        return styles.result;
      }
    }

    return styles.Odds;
  };

  const getOverStyle = () => {
    const total = settled ? sum(fixture.home_points, fixture.away_points) : null;
    if (total === null || !isNum(fixture.over)) return styles.Odds;
    if (mode === "Recents" && total >= fixture.over) {
      return styles.Suggested;
    }
    if (mode === "HeadtoHead" && total >= fixture.over) {
      return styles.result;
    }
    return styles.Odds;
  };

  const getAwaySpreadStyle = () => {
    if (mode === "Recents") {
      let teamPoints, opponentPoints, spread;
      if (selectedTeam === fixture.away_team) {
        teamPoints = fixture.away_points;
        opponentPoints = fixture.home_points;
        spread = fixture.away_spread;
        
        if (settled && isNum(spread) && teamPoints + spread >= opponentPoints) {
          return styles.Suggested;
        }
      }
    }
    if (mode === "HeadtoHead") {
      if (settled && isNum(fixture.away_spread) &&
          fixture.away_points + fixture.away_spread >= fixture.home_points) {
        return styles.result;
      }
    }
    return styles.Odds;
  };

  const getAwayMLStyle = () => {
    if (mode === "Recents") {
      let teamPoints, opponentPoints;
      if (selectedTeam === fixture.away_team) {
        teamPoints = fixture.away_points;
        opponentPoints = fixture.home_points;
        
        if (settled && teamPoints > opponentPoints) {
          return styles.Suggested;
        }
      }
    }
    if (mode === "HeadtoHead") {
      if (settled && fixture.away_points > fixture.home_points) {
        return styles.result;
      }
    }
    return styles.Odds;
  };

  const getUnderStyle = () => {
    const total = settled ? sum(fixture.home_points, fixture.away_points) : null;
    if (total === null || !isNum(fixture.under)) return styles.Odds;
    if (mode === "Recents" && total <= fixture.under) {
      return styles.Suggested;
    }
    if (mode === "HeadtoHead" && total <= fixture.under) {
      return styles.result;
    }
    return styles.Odds;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0}}
      transition={{
        duration: 0.5,
        scale: { type: "spring", damping: 10, stiffness: 100, bounce: 0.5 }
      }}
    >
      <Table className="main" bordered onClick={() => { hapticTap(); if (onClick) onClick(); }}>
        <tbody>
          <tr>
            <td className="Odds" style={styles.Odds}> Home </td>
            <td className="Odds" style={styles.Odds}> {fixture.home_team} </td>
            <td className="Odds" style={getHomeSpreadStyle()}>
              {line(fixture.home_spread, fixture.home_spread_price)}
            </td>
            <td className="Odds" style={getHomeMLStyle()}>
              {price(fixture.home_moneyline)}
            </td>
            <td className="Odds" style={getOverStyle()}>
              {line(fixture.over, fixture.over_price)}
            </td>
          </tr>
          <tr>
            <td className="Odds" style={styles.Odds}> Away </td>
            <td className="Odds" style={styles.Odds}> {fixture.away_team} </td>
            <td className="Odds" style={getAwaySpreadStyle()}>
              {line(fixture.away_spread, fixture.away_spread_price)}
            </td>
            <td className="Odds" style={getAwayMLStyle()}>
              {price(fixture.away_moneyline)}
            </td>
            <td className="Odds" style={getUnderStyle()}>
              {line(fixture.under, fixture.under_price)}
            </td>
          </tr>
          {/* A draw has a moneyline and nothing else. The team, spread and
              total cells are blanked and unboxed rather than em-dashed: an
              em-dash is this app's word for "unknown", and these are not
              unknown, they are inapplicable. See styles.drawBlank. */}
          {isNum(fixture.draw_moneyline) && (
            <tr style={styles.drawRow}>
              <td className="Odds" style={{ ...styles.Odds, ...styles.drawCell }}> Draw </td>
              <td style={styles.drawBlank} />
              <td style={styles.drawBlank} />
              <td className="Odds" style={{ ...getDrawMLStyle(), ...styles.drawCell }}>
                {price(fixture.draw_moneyline)}
              </td>
              <td style={styles.drawBlank} />
            </tr>
          )}
        </tbody>
      </Table>
    </motion.div>
  );
};

export default OddsTable;